import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

/*  tweddi — Verify Email (/verify-email)
    tweddi identity: cream ground, Inclusive Sans, near-mono ink, apricot only
    as a rare tiny accent (the ✳), the woven monogram wordmark, circle-arrow
    CTA. Idempotent verify (hasRun guard) so React StrictMode's double-render
    can't turn success into an error. Also honours a pending event code
    (?event_code= in the link, or localStorage 'pendingEventCode') and joins
    that event before continuing.                                            */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CREAM = '#FFFDF8';
const WASH  = '#FBF6EF';
const INK   = '#141313';
const MUTE  = '#6E6B62';
const APRICOT = '#F4A26B';
const RULE  = 'rgba(20,19,19,0.10)';

const Mark = ({ size = 26, mark = INK, bg = CREAM }) => (
  <svg viewBox="-60 -60 664 520" width={size} height={size * (520/664)} style={{ overflow: 'visible', display: 'block', flexShrink: 0 }} aria-label="tweddi">
    <circle cx="200" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={bg}   strokeWidth="108" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <path d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" stroke={bg}   strokeWidth="108" />
    <path d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" stroke={mark} strokeWidth="66" />
  </svg>
);

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const eventCodeFromUrl = searchParams.get('event_code');
  const { user, updateUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [nextUser, setNextUser] = useState(null); // freshest user for routing on Continue
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;      // guard against StrictMode / re-render double fire
    hasRun.current = true;
    if (!token) { setStatus('error'); setMessage('This verification link is missing its token.'); return; }
    (async () => {
      try {
        const res = await axios.post(`${API}/auth/verify-email`, { token });
        // idempotent server: already-verified also returns ok
        let freshUser = res.data?.user || null;
        if (freshUser) updateUser(freshUser);

        // Honour a pending event code (from the link or from earlier in the flow)
        const pendingCode = eventCodeFromUrl || localStorage.getItem('pendingEventCode');
        if (pendingCode) {
          try {
            const r = await axios.post(`${API}/events/join`, { event_code: pendingCode });
            updateUser({ event_id: r.data.event.id });
            freshUser = { ...(freshUser || {}), event_id: r.data.event.id };
          } catch {}
          finally { localStorage.removeItem('pendingEventCode'); }
        }

        setNextUser(freshUser);
        setStatus('success');
        setMessage(res.data?.already_verified ? 'Your email was already verified.' : 'Your email is verified.');
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'This verification link is invalid or has expired.');
      }
    })();
  }, [token, eventCodeFromUrl, updateUser]);

  const goNext = () => {
    const u = nextUser || user;
    if (u?.is_host) navigate('/admin');
    else if (u?.event_id && u?.profile_complete) navigate('/discover');
    else if (u?.event_id) navigate('/profile-setup');
    else navigate('/');
  };

  const eyebrow = status === 'verifying' ? 'One moment' : status === 'success' ? 'Verified' : 'Link issue';
  const headline = status === 'verifying' ? <>Verifying your email…</> : status === 'success' ? <>You're in.</> : <>Link expired.</>;

  return (
    <div style={{ background: `linear-gradient(180deg, ${CREAM} 55%, ${WASH} 100%)`, minHeight: '100dvh', maxWidth: '30rem', margin: '0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '2rem 1.75rem' }} data-testid="verify-email-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inclusive+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap');
        * { box-sizing: border-box; }
        .ve-circle { transition: transform 0.25s cubic-bezier(.16,1,.3,1); }
        .ve-cta:hover .ve-circle { transform: translateX(3px); }
        .ve-tw { display: inline-block; animation: ve-tw 2.6s ease-in-out infinite; transform-origin: center; }
        @keyframes ve-tw { 0%,100%{transform:scale(1) rotate(0);opacity:.7} 50%{transform:scale(1.3) rotate(90deg);opacity:1} }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .ve-tw { animation: none; } }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '3rem' }}>
        <Mark size={26} mark={INK} bg={CREAM} />
        <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.15rem', fontWeight: 600, color: INK, letterSpacing: '-0.01em' }}>tweddi</span>
      </div>

      <div style={{ borderTop: `1px solid ${INK}`, paddingTop: '1.5rem', marginBottom: '2rem' }}>
        <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTE, marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          {eyebrow} {status === 'success' && <span className="ve-tw" style={{ fontSize: '0.7rem', color: APRICOT }}>✳</span>}
        </p>
        <h1 style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: 'clamp(2.3rem, 9vw, 3rem)', fontWeight: 600, color: INK, lineHeight: 1.03, letterSpacing: '-0.03em' }}>
          {headline}
        </h1>
      </div>

      {status === 'verifying' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <div className="spin" style={{ width: '1.1rem', height: '1.1rem', border: `1.5px solid ${RULE}`, borderTopColor: INK, borderRadius: '50%' }} />
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.76rem', fontWeight: 500, color: MUTE }}>Just a second while we confirm your link.</p>
        </div>
      )}

      {status === 'success' && (
        <div>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.1rem', fontStyle: 'italic', color: INK, marginBottom: '0.75rem' }}>{message}</p>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.76rem', fontWeight: 500, color: MUTE, lineHeight: 1.7, marginBottom: '2rem' }}>
            Your account is ready. Step inside and meet the room.
          </p>
          <button onClick={goNext} data-testid="verify-continue-btn" className="ve-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK }}>
            Continue
            <span className="ve-circle" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '2.2rem', height: '2.2rem', borderRadius: '50%', background: INK }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={CREAM} strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </span>
          </button>
        </div>
      )}

      {status === 'error' && (
        <div>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.76rem', fontWeight: 500, color: MUTE, lineHeight: 1.7, marginBottom: '1.5rem' }}>{message}</p>
          <button onClick={() => navigate('/auth')} data-testid="verify-back-btn" style={{ background: 'none', border: 'none', borderBottom: `1.5px solid ${INK}`, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.64rem', fontWeight: 600, letterSpacing: '0.08em', color: INK, padding: '0 0 2px' }}>
            Back to sign in →
          </button>
        </div>
      )}

      <div style={{ marginTop: '3rem', paddingTop: '1.5rem', borderTop: `1px solid rgba(20,19,19,0.06)`, display: 'flex', gap: '1.25rem' }}>
        <a href="/terms" style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.58rem', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTE, textDecoration: 'none' }}>Terms</a>
        <a href="/privacy" style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.58rem', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTE, textDecoration: 'none' }}>Privacy</a>
      </div>
    </div>
  );
}
